import React, { useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import useCustomerEventForm from '../hooks/inbox/forms/useCustomerEventForm';
import DropdownMC from '../components/DropdownMC';
import RightModalLayout from '../layouts/Modal/RightModalLayout';
import FormProviderMC from '../components/FormProviderMC';
import InputComponent from '../components/InputMC';
import MessageCounterMC from '../modules/inbox/components/MessageCounterMC';

import useCustomerEventSubcategoryOptions from '../hooks/inbox/data/useCustomerEventSubcategoryOptions';
import useCustomerEventActions from '../hooks/inbox/actions/useCustomerEventActions';
import { useTraceUpdate } from '../hooks/shared/useDebugRenders';
import { SIDEBAR_ACTIONS } from '../../constants/actionsConstants';

const CustomerEventSidebar = ({ customer, chatId }) => {
  const dispatch = useDispatch();
  // Debug tracking de dependencias críticas
  useTraceUpdate('CustomerEventSidebar', {
    customerId: customer?.web_id
  }, {
    logRenderCount: true,
    logPropChanges: true
  });

  const { t } = useTranslation('InboxMC');

  // Hook de forms layer para validación y estado del formulario
  const { formMethods, maxChars } = useCustomerEventForm(customer);

  // Opciones de subcategoría (data layer, traducidas)
  const subcategoryOptions = useCustomerEventSubcategoryOptions();

  const closeModal = useCallback(() => {
    dispatch({
      type: SIDEBAR_ACTIONS.CLOSE_SIDEBAR,
      payload: { componentKey: 'CustomerEventSidebar' }
    });
  }, [dispatch]);

  // Hook de capa de acciones (Actions Layer)
  const {
    loading,
    handleSaveButtonClick
  } = useCustomerEventActions({
    formMethods,
    subcategoryOptions,
    closeModal,
    chatId
  });

  const modalConfig = useMemo(() => ({
    title: t('event.modal_title'),
    submitLabel: loading ? t('event.saving') : t('event.save'),
    submitAction: handleSaveButtonClick
  }), [loading, t, handleSaveButtonClick]);

  // Renderizado
  return (
    <div className="tw-wrapper">
      <RightModalLayout
        title={modalConfig.title}
        closeAction={closeModal}
        submitLabel={modalConfig.submitLabel}
        submitAction={modalConfig.submitAction}
      >
        <div className="tw-flex-1 tw-overflow-auto">
          <FormProviderMC methods={formMethods}>
            <form id="customer-event-form" onSubmit={formMethods.handleSubmit(handleSaveButtonClick)} className="tw-h-full tw-flex tw-flex-col">
              <div className="tw-p-2 tw-flex-1 tw-flex tw-flex-col">
                <div className="tw-mb-4">
                  <div className="tw-text-sm tw-font-medium tw-mb-2">{t('event.activity_type_label')}</div>
                  <DropdownMC
                    useFloatingLabel
                    options={subcategoryOptions}
                    label={t('event.activity_label')}
                    name="subcategory"
                    className="tw-mb-3"
                    renderOption={option => option?.renderLabel}
                    renderSelected={option => option?.renderLabel || t('customerDetails.selectPlaceholder')}
                  />
                </div>
                <div className="tw-flex-1 tw-flex tw-flex-col">
                  <div className="tw-text-sm tw-font-medium tw-mb-2">{t('event.activity_description_label')}</div>
                  <div className="tw-border tw-border-gray-200 tw-rounded-xl tw-p-1 tw-flex tw-flex-col tw-gap-1 tw-items-stretch tw-w-full tw-flex-1 tw-min-w-0 tw-bg-gray-50">
                    <div className="tw-flex tw-w-full tw-items-start tw-gap-2 tw-flex-1">
                      <InputComponent
                        textarea
                        useFloatingLabel
                        hideBorder
                        rows={4}
                        label={t('event.activity_description_label')}
                        name="action"
                        placeholder={t('event.description_placeholder')}
                        maxLength={maxChars}
                        floatingLabelClassName="tw-start-3"
                        containerClass="tw-flex-1 tw-min-w-0 tw-h-full"
                        className="tw-bg-transparent tw-flex-1 tw-min-w-0 tw-h-full tw-resize-none tw-placeholder-gray-400 focus:tw-ring-0 focus:tw-border-transparent focus:tw-shadow-none focus:tw-outline-none focus:tw-ring-transparent focus:tw-border-0 !tw-px-3"
                      />
                    </div>
                    <MessageCounterMC name="action" methods={formMethods} maxChars={maxChars} />
                  </div>
                </div>
              </div>
            </form>
          </FormProviderMC>
        </div>
      </RightModalLayout>
    </div>
  );
};

export default CustomerEventSidebar;
